import styled from "styled-components";
import { PortalActionBar, PortalMetricCard, PortalSurface, portalColors } from "./BusinessPortalComponents";

export const PAYMENT_FLOW_MAX_WIDTH = "64rem";

export const PortalFormSurface = styled(PortalSurface)`
    max-width: ${p => p.maxWidth || "84.688rem"};
    margin-top: ${p => p.marginTop || "1rem"};
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: ${p => p.flex || "0 1 auto"};
    min-height: ${p => p.minHeight || "0"};
    max-height: ${p => p.maxHeight || "none"};
`;

export const PortalFormBody = styled.div`
    display: grid;
    grid-template-columns: ${p => p.columns || "minmax(0, 1fr)"};
    gap: ${p => p.gap || "1.25rem"};
    align-items: ${p => p.alignItems || "stretch"};
    padding: ${p => p.padding || "1.25rem"};
    height: ${p => p.height || "auto"};
    min-height: ${p => p.minHeight || "0"};
    overflow: ${p => p.overflow || "visible"};
    box-sizing: border-box;

    @media (max-width: ${p => p.breakpoint || "980px"}) {
        grid-template-columns: 1fr;
        padding: ${p => p.mobilePadding || "1rem"};
    }
`;

export const PortalFormScrollBody = styled(PortalFormBody)`
    overflow-y: auto;
    min-height: 0;
    flex: 1 1 auto;
    max-height: ${p => p.maxHeight || "none"};
`;

export const PortalFormSection = styled.section`
    min-width: 0;
    min-height: ${p => p.minHeight || "0"};
    display: grid;
    gap: ${p => p.gap || "1rem"};
    align-content: start;
    padding: ${p => p.padding || "1rem"};
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: ${p => p.radius || "12px"};
    overflow: ${p => p.overflow || "visible"};
    box-sizing: border-box;
`;

export const PortalFormSectionHeader = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.35rem;
    padding-bottom: ${p => p.withDivider ? "0.85rem" : "0"};
    border-bottom: ${p => p.withDivider ? `1px solid ${portalColors.border}` : "none"};
`;

export const PortalFormSectionTitle = styled.h2`
    margin: 0;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "1rem"};
    font-weight: 800;
    line-height: 1.3;
`;

export const PortalFormSectionDescription = styled.p`
    margin: ${p => p.margin || "0"};
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    font-weight: ${p => p.weight || 500};
    line-height: 1.5;
`;

export const PortalFormFieldGroup = styled.div`
    display: grid;
    gap: ${p => p.gap || "0"};
    min-width: 0;
    align-content: start;

    > label,
    > p {
        margin: 10px 0 0 0rem;
    }
`;

export const PortalFormFieldRow = styled.div`
    display: grid;
    grid-template-columns: ${p => p.columns || "repeat(2, minmax(0, 1fr))"};
    gap: ${p => p.gap || "1rem"};
    align-items: ${p => p.alignItems || "start"};
    min-width: 0;

    @media (max-width: ${p => p.breakpoint || "720px"}) {
        grid-template-columns: 1fr;
    }
`;

export const PortalFormLabel = styled.label`
    margin: ${p => p.margin || "0"};
    color: ${p => p.color || portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.fontSize || p.size || "1rem"};
    font-weight: ${p => p.fontWeight || p.weight || 700};
    line-height: 1.3;
`;

export const PortalFormDescription = styled.p`
    margin: ${p => p.margin || "0.25rem 0 0.35rem"};
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    font-weight: ${p => p.fontWeight || p.weight || 500};
    line-height: 1.5;
    text-align: ${p => p.align || "left"};
`;

export const PortalFormTextArea = styled.textarea`
    width: 100%;
    min-height: ${p => p.minHeight || "8.5rem"};
    height: ${p => p.height || "auto"};
    padding: ${p => p.padding || "0.75rem 1rem"};
    border: ${p => p.isHighlighted ? `1px solid ${portalColors.danger}` : `1px solid ${portalColors.borderStrong}`};
    border-radius: 8px;
    background: ${p => p.background || portalColors.surfaceSoft};
    box-shadow: none;
    box-sizing: border-box;
    color: ${portalColors.text};
    caret-color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.9375rem"};
    font-weight: 400;
    line-height: 1.45;
    resize: ${p => p.resize || "vertical"};

    ::placeholder {
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: ${p => p.placeholderSize || "0.9375rem"};
        font-weight: 400;
    }

    &:focus {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
        outline: none;
    }

    &:disabled {
        background: ${portalColors.surfaceTertiary};
        color: ${portalColors.textSoft};
        cursor: not-allowed;
    }
`;

export const PortalOptionGrid = styled.div`
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    gap: ${p => p.gap || "0.5rem"};
`;

export const PortalOptionButton = styled.button`
    position: relative;
    width: ${p => p.width || "9.6rem"};
    min-height: ${p => p.height || "2.9rem"};
    padding: ${p => p.padding || "0.65rem 2.15rem 0.65rem 0.85rem"};
    border: 1px solid ${p => p.active ? portalColors.text : portalColors.border};
    border-radius: 8px;
    background: ${p => p.active ? portalColors.blueSoft : portalColors.surfaceSoft};
    color: ${portalColors.text};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: ${p => p.active ? 800 : 600};
    line-height: 1.2;
    text-align: left;
    opacity: ${p => p.disabled ? 0.62 : 1};
    box-sizing: border-box;
    transition: background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;

    &:hover {
        border-color: ${portalColors.text};
    }

    &::after {
        content: "${p => p.active ? "✓" : ""}";
        position: absolute;
        top: 50%;
        right: 0.75rem;
        width: 1.15rem;
        height: 1.15rem;
        transform: translateY(-50%);
        border-radius: 999px;
        background: ${p => p.active ? portalColors.text : "transparent"};
        color: var(--calendar-bg, #FFFFFF);
        display: ${p => p.active ? "inline-flex" : "none"};
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 800;
        line-height: 1;
    }
`;

export const PortalPaymentOptionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${p => p.gap || "0.75rem"};
    width: 100%;
    margin: ${p => p.margin || "0 0 1rem"};

    @media (max-width: ${p => p.breakpoint || "640px"}) {
        grid-template-columns: 1fr;
    }
`;

export const PortalPaymentOptionButton = styled.button`
    position: relative;
    width: 100%;
    min-height: ${p => p.minHeight || "4.25rem"};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 0.85rem 2.75rem 0.85rem 1rem;
    border: 1px solid ${p => p.active || p.$active ? portalColors.text : portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    background: ${p => p.active || p.$active ? portalColors.text : portalColors.surfaceSoft};
    color: ${p => p.active || p.$active ? "var(--calendar-bg, #FFFFFF)" : portalColors.text};
    box-shadow: ${portalColors.inputShadow};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.62 : 1};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;

    &:hover {
        border-color: ${portalColors.text};
    }
`;

export const PortalPaymentOptionIndicator = styled.span`
    position: absolute;
    right: 0.75rem;
    top: 50%;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background-color: #76C00D;
    display: ${p => p.active || p.$active ? "inline-flex" : "none"};
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    color: #fff;
    font-size: 0.625rem;
    font-weight: 700;

    &::before {
        content: "\\2713";
    }
`;

export const PortalPaymentSummaryCard = styled(PortalMetricCard)`
    width: 100%;
    max-width: 100%;
    min-height: auto;
    align-content: start;
    gap: 0.4rem;
    overflow: hidden;
    background: linear-gradient(135deg, rgba(91, 110, 245, 0.14), rgba(66, 231, 182, 0.12));
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
`;

export const PortalPaymentSummaryRow = styled.div`
    min-width: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 1rem;
`;

export const PortalPaymentSummaryLabel = styled.div`
    min-width: 0;
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.75rem"};
    font-weight: ${p => p.weight || 800};
    line-height: 1.25;
    text-transform: uppercase;
`;

export const PortalPaymentSummaryAmount = styled.div`
    min-width: 0;
    color: ${p => p.color || portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "1rem"};
    font-weight: ${p => p.weight || 800};
    line-height: 1.25;
    text-align: right;
    overflow-wrap: anywhere;
`;

export const PortalSelectedItem = styled.div`
    min-height: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    box-sizing: border-box;
`;

export const PortalSelectedItemContent = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.65rem;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
`;

export const PortalSelectedIcon = styled.div`
    width: 2rem;
    height: 2rem;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${portalColors.surface};
    border-radius: 999px;
    background: ${portalColors.surfaceTertiary};
    color: ${portalColors.mutedText};
`;

export const PortalFormLinkButton = styled.button`
    border: 0;
    background: transparent;
    color: ${portalColors.text};
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    padding: 0;
`;

export const PortalUploadZone = styled.div`
    width: ${p => p.width || "min(100%, 20rem)"};
    min-height: ${p => p.minHeight || "11.25rem"};
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    padding: ${p => p.padding || "1rem"};
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    background: ${portalColors.surfaceSoft};
    box-sizing: border-box;
    overflow: hidden;
`;

export const PortalFormActionsSpacer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-left: auto;
`;

export const PortalFormActions = styled(PortalActionBar)`
    position: ${p => p.sticky === false ? "static" : "sticky"};
    bottom: 0;
    z-index: 3;
    border-width: 1px 0 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
    border-radius: 0;
    box-shadow: none;
    justify-content: flex-end;
    background: ${portalColors.surface};
`;
